"use client";

// packages/react/src/use-resize-observer/index.ts
import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "just-debounce-it";
var defaultDebounceDelay = 500;
function setDebounce(delay) {
  defaultDebounceDelay = delay;
}
function useResizeObserver({
  lazy = false,
  debounce: debounceDelay = defaultDebounceDelay,
  options = {},
  callback = () => {
  }
} = {}, deps = []) {
  const [element, setElement] = useState();
  const [entry, setEntry] = useState();
  const entryRef = useRef();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    if (!element) return;
    let immediate = true;
    function emit(entry2) {
      callbackRef.current(entry2);
      entryRef.current = entry2;
      if (!lazy) {
        setEntry(entry2);
      }
    }
    const debouncedEmit = debounce(emit, debounceDelay);
    function onResize(entries) {
      const entry2 = entries[0];
      if (entry2) {
        if (immediate) {
          emit(entry2);
        } else {
          debouncedEmit(entry2);
        }
        immediate = false;
      }
    }
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(element, options);
    return () => {
      resizeObserver.disconnect();
    };
  }, [element, debounceDelay, lazy, JSON.stringify(options), ...deps]);
  const getEntryRef = useCallback(() => entryRef.current, []);
  return [setElement, lazy ? getEntryRef : entry];
}
useResizeObserver.setDebounce = setDebounce;

// packages/react/src/use-window-size/index.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
import debounce2 from "just-debounce-it";
var defaultDebounceDelay2 = 500;
function setDebounce2(delay) {
  defaultDebounceDelay2 = delay;
}
function useWindowSize(debounceDelay = defaultDebounceDelay2) {
  const [width, setWidth] = useState2();
  const [height, setHeight] = useState2();
  const [dpr, setDpr] = useState2();
  useEffect2(() => {
    function onWindowResize() {
      setWidth(
        Math.min(window.innerWidth, document.documentElement.clientWidth)
      );
      setHeight(
        Math.min(window.innerHeight, document.documentElement.clientHeight)
      );
      setDpr(window.devicePixelRatio);
    }
    const debouncedOnWindowRezise = debounce2(onWindowResize, debounceDelay);
    window.addEventListener("resize", debouncedOnWindowRezise, false);
    onWindowResize();
    return () => {
      window.removeEventListener("resize", debouncedOnWindowRezise, false);
      debouncedOnWindowRezise.cancel();
    };
  }, [debounceDelay]);
  return { width, height, dpr };
}
useWindowSize.setDebounce = setDebounce2;

// packages/react/src/use-media-query/index.ts
import { useEffect as useEffect3, useState as useState3 } from "react";
function useMediaQuery(query) {
  const [isMatch, setIsMatch] = useState3();
  useEffect3(() => {
    const mediaQuery = window.matchMedia(query);
    function onChange() {
      setIsMatch(mediaQuery.matches);
    }
    mediaQuery.addEventListener("change", onChange, false);
    onChange();
    return () => mediaQuery.removeEventListener("change", onChange, false);
  }, [query]);
  return isMatch;
}

// packages/react/src/use-lazy-state/index.ts
import { useCallback as useCallback2, useEffect as useEffect4, useRef as useRef2 } from "react";
function useLazyState(initialValue, callback, deps = []) {
  const prevStateRef = useRef2();
  const stateRef = useRef2(initialValue);
  const callbackRef = useRef2(callback);
  callbackRef.current = callback;
  useEffect4(() => {
    callbackRef.current(stateRef.current, prevStateRef.current);
  }, [initialValue, ...deps]);
  function set(value) {
    if (typeof value === "function") {
      const nextValue = value(stateRef.current);
      callbackRef.current(nextValue, stateRef.current);
      stateRef.current = nextValue;
      return;
    }
    if (value !== stateRef.current) {
      callbackRef.current(value, stateRef.current);
      stateRef.current = value;
    }
  }
  const get = useCallback2(() => stateRef.current, []);
  return [set, get];
}

// packages/react/src/use-rect/index.ts
import { useEffect as useEffect5, useCallback as useCallback3, useRef as useRef3, useState as useState4 } from "react";

// packages/react/src/use-rect/utils.ts
function removeParentSticky(element) {
  const position = getComputedStyle(element).position;
  const isSticky = position === "sticky";
  if (isSticky) {
    element.style.setProperty("position", "relative");
    element.dataset.sticky = "true";
  }
  if (element.offsetParent) {
    removeParentSticky(element.offsetParent);
  }
}
function addParentSticky(element) {
  if (element?.dataset?.sticky === "true") {
    element.style.removeProperty("position");
    delete element.dataset.sticky;
  }
  if (element.parentNode) {
    addParentSticky(element.parentNode);
  }
}
function offsetTop(element, accumulator = 0) {
  const top = accumulator + element.offsetTop;
  if (element.offsetParent) {
    return offsetTop(element.offsetParent, top);
  }
  return top;
}
function offsetLeft(element, accumulator = 0) {
  const left = accumulator + element.offsetLeft;
  if (element.offsetParent) {
    return offsetLeft(element.offsetParent, left);
  }
  return left;
}
function scrollTop(element, accumulator = 0) {
  const top = accumulator + (element?.scrollTop ?? 0);
  if (element.parentNode) {
    return scrollTop(element.parentNode, top);
  }
  return top;
}
function scrollLeft(element, accumulator = 0) {
  const left = accumulator + (element?.scrollLeft ?? 0);
  if (element.parentNode) {
    return scrollLeft(element.parentNode, left);
  }
  return left;
}

// packages/react/src/use-rect/emitter.ts
import { createNanoEvents } from "nanoevents";
var emitter = createNanoEvents();

// packages/react/src/use-rect/index.ts
var defaultDebounceDelay3 = 500;
function setDebounce3(delay) {
  defaultDebounceDelay3 = delay;
}
function useRect({
  ignoreTransform = false,
  ignoreSticky = true,
  debounce: debounceDelay = defaultDebounceDelay3,
  lazy = false,
  callback
} = {}, deps = []) {
  const [wrapperElement, setWrapperElement] = useState4(null);
  const [element, setElement] = useState4(null);
  const callbackRef = useRef3(callback);
  callbackRef.current = callback;
  const updateRect = useCallback3(
    ({
      top,
      left,
      width,
      height,
      element: element2
    }) => {
      top = top ?? rectRef.current.top;
      left = left ?? rectRef.current.left;
      width = width ?? rectRef.current.width;
      height = height ?? rectRef.current.height;
      element2 = element2 ?? rectRef.current.element;
      if (top === rectRef.current.top && left === rectRef.current.left && width === rectRef.current.width && height === rectRef.current.height && element2 === rectRef.current.element)
        return;
      const y = top;
      const x = left;
      let bottom;
      let right;
      if (top !== void 0 && height !== void 0) bottom = top + height;
      if (left !== void 0 && width !== void 0) right = left + width;
      rectRef.current = {
        ...rectRef.current,
        top,
        y,
        left,
        x,
        width,
        height,
        bottom,
        right,
        element: element2
      };
      callbackRef.current?.(rectRef.current);
      if (!lazy) {
        setRect(rectRef.current);
      }
    },
    [lazy, ...deps]
  );
  const computeCoordinates = useCallback3(() => {
    if (!element || !wrapperElement) return;
    let top;
    let left;
    if (ignoreSticky) removeParentSticky(element);
    if (ignoreTransform) {
      top = offsetTop(element);
      left = offsetLeft(element);
    } else {
      const rect2 = element.getBoundingClientRect();
      top = rect2.top + scrollTop(wrapperElement);
      left = rect2.left + scrollLeft(wrapperElement);
    }
    if (ignoreSticky) addParentSticky(element);
    updateRect({
      top,
      left
    });
  }, [element, ignoreSticky, ignoreTransform, wrapperElement, updateRect]);
  const computeDimensions = useCallback3(() => {
    if (!element) return;
    const rect2 = element.getBoundingClientRect();
    const width = rect2.width;
    const height = rect2.height;
    updateRect({ width, height });
  }, [element, updateRect]);
  const resize = useCallback3(() => {
    computeCoordinates();
    computeDimensions();
  }, [computeCoordinates, computeDimensions]);
  const rectRef = useRef3({});
  const [rect, setRect] = useState4({});
  useEffect5(() => {
    rectRef.current.resize = resize;
    setRect(rectRef.current);
    return emitter.on("resize", resize);
  }, [resize]);
  const [setResizeObserverRef] = useResizeObserver(
    {
      lazy: true,
      debounce: debounceDelay,
      callback: (entry) => {
        if (!entry) return;
        const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0] ?? {};
        updateRect({
          width,
          height
        });
      }
    },
    [element, lazy, updateRect]
  );
  const [setWrapperResizeObserverRef] = useResizeObserver(
    {
      lazy: true,
      debounce: debounceDelay,
      callback: computeCoordinates
    },
    [computeCoordinates]
  );
  useEffect5(() => {
    setWrapperResizeObserverRef((v) => {
      if (v && v !== document.body) return v;
      return document.body;
    });
    setWrapperElement((v) => {
      if (v && v !== document.body) return v;
      return document.body;
    });
  }, [setWrapperResizeObserverRef]);
  const getRectRef = useCallback3(() => rectRef.current, []);
  const setElementRef = useCallback3(
    (node) => {
      setResizeObserverRef(node);
      setElement(node);
      updateRect({
        element: node
      });
    },
    [setResizeObserverRef, updateRect]
  );
  const setWrapperElementRef = useCallback3(
    (node) => {
      setWrapperResizeObserverRef(node);
      setWrapperElement(node);
    },
    [setWrapperResizeObserverRef]
  );
  return [setElementRef, lazy ? getRectRef : rect, setWrapperElementRef];
}
useRect.resize = () => emitter.emit("resize");
useRect.setDebounce = setDebounce3;

// packages/react/src/use-timeout/index.ts
import { useEffect as useEffect6, useRef as useRef4 } from "react";
function useTimeout(callback, delay, deps = []) {
  const callbackRef = useRef4(callback);
  callbackRef.current = callback;
  useEffect6(() => {
    const timeout = setTimeout(callbackRef.current, delay);
    return () => clearTimeout(timeout);
  }, [delay, ...deps]);
}

// packages/react/src/use-object-fit/index.ts
import { useMemo } from "react";
function useObjectFit(parentWidth = 1, parentHeight = 1, childWidth = 1, childHeight = 1, objectFit = "cover") {
  const [width, height] = useMemo(() => {
    if (!parentWidth || !parentHeight || !childWidth || !childHeight)
      return [1, 1];
    const parentRatio = parentWidth / parentHeight;
    const childRatio = childWidth / childHeight;
    let width2;
    if (objectFit === "contain") {
      width2 = parentRatio > childRatio ? parentHeight * childRatio : parentWidth;
    } else if (objectFit === "cover") {
      width2 = parentRatio > childRatio ? parentWidth : parentHeight * childRatio;
    } else {
      return [1, 1];
    }
    const height2 = width2 / childRatio;
    return [parentWidth / width2, parentHeight / height2];
  }, [parentWidth, parentHeight, childHeight, childWidth, objectFit]);
  return [1 / width, 1 / height];
}

// packages/react/src/use-intersection-observer/index.ts
import { useCallback as useCallback4, useEffect as useEffect7, useRef as useRef5, useState as useState6 } from "react";
function useIntersectionObserver({
  root = null,
  rootMargin = "0px",
  threshold = 0,
  once = false,
  lazy = false,
  callback = () => {
  }
} = {}, deps = []) {
  const entryRef = useRef5();
  const [entry, setEntry] = useState6();
  const [element, setElement] = useState6();
  useEffect7(() => {
    if (!element) return;
    const intersection = new IntersectionObserver(
      ([entry2]) => {
        if (lazy) {
          entryRef.current = entry2;
        } else {
          setEntry(entry2);
        }
        callback(entry2);
        if (once && entry2?.isIntersecting) intersection.disconnect();
      },
      {
        root,
        rootMargin,
        threshold
      }
    );
    intersection.observe(element);
    return () => {
      intersection.disconnect();
    };
  }, [element, root, rootMargin, threshold, lazy, once, ...deps]);
  const get = useCallback4(() => entryRef.current, []);
  return [
    setElement,
    lazy ? get : entry
    // fix type
  ];
}
export {
  useIntersectionObserver,
  useLazyState,
  useMediaQuery,
  useObjectFit,
  useRect,
  useResizeObserver,
  useTimeout,
  useWindowSize
};
//# sourceMappingURL=hamo.mjs.map